import { RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import GlobalLoader from 'components/GlobalLoader';

import { AuthContext } from '../';
import { ERROR_CONSENT_REQUIRED, ERROR_LOGIN_REQUIRED, FORCE_LOGIN_PARAM } from './constants';

interface AuthSessionProps {
  children: React.ReactNode;
}

const AuthSession = ({ children }: AuthSessionProps) => {
  const { getAccessTokenSilently, isLoading, loginWithRedirect } = useAuth0();
  const { auth, authDispatch } = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    const getTokenFromAuth0 = async () => {
      try {
        const fullToken = await getAccessTokenSilently({
          detailedResponse: true,
        });

        const { access_token } = fullToken;

        authDispatch({ token: access_token, type: 'auth0' });
      } catch (err) {
        // when trying to auth with auth0 locally, you will need to provide consent (via login)
        // to the life-web app, even if you're already 'logged in' to policygenius via auth0
        if (
          process.env.NODE_ENV !== 'production' &&
          err instanceof Error &&
          err.message === ERROR_CONSENT_REQUIRED
        ) {
          return loginWithRedirect({ passwordless: true });
        }

        if (err instanceof Error && err.message === ERROR_LOGIN_REQUIRED) {
          const forceLoginOptions = getForceLoginOptions();

          if (forceLoginOptions) {
            return loginWithRedirect(forceLoginOptions);
          }

          const anonToken = await getAnonToken();

          if (anonToken) {
            return authDispatch({ token: anonToken.access_token_v2, type: 'anon' });
          }
        }

        if (!auth.token) {
          authDispatch({ type: 'none' });
        }
      }
    };

    const getAnonToken = async () => {
      const anonToken: {
        access_token_v2: string;
        id_token: string;
        token_type: string;
      } = await fetch('/token/anon', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }

          throw `Error fetching anonToken: ${res.status} (${res.statusText})`;
        })
        .catch(console.error);

      return anonToken;
    };

    const getForceLoginOptions = (): RedirectLoginOptions | false => {
      const { pathname, search } = location;
      const params = new URLSearchParams(search);

      const forceLoginParamPresent = params.get(FORCE_LOGIN_PARAM) === 'true';

      if (forceLoginParamPresent) {
        params.delete(FORCE_LOGIN_PARAM);
        const paramString = params.toString();
        const nextSearchString = paramString.length > 0 ? `?${paramString}` : '';

        return {
          passwordless: true,
          appState: {
            returnTo: `${pathname}${nextSearchString}`,
          },
        };
      }

      return false;
    };

    if (window.Cypress) {
      const cypressToken = window.localStorage.getItem('auth0Cypress');

      // If this is a test where we signed in, use the token we stored. Otherwise
      // run `getTokenFromAuth0()` which will ultimately get the anon token
      if (cypressToken) {
        authDispatch({ token: cypressToken, type: 'auth0' });
        // Remove the stored token so that it doesn't persist after switching users
        // in the SelectUser test. This is fine because we login before each
        // test where we want to be logged in.
        window.localStorage.removeItem('auth0Cypress');
      } else {
        getTokenFromAuth0();
      }
    } else {
      getTokenFromAuth0();
    }
  }, [getAccessTokenSilently, location.pathname]);

  if (isLoading && !auth.type) {
    return <GlobalLoader />;
  }

  return <>{children}</>;
};

export default AuthSession;
